import { Button } from "../ui/button"
import { useCallback, useState, useMemo, useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { getContacts, getTags, deleteTag, patchContact, deleteContact } from "services/contacts"
import { getTasks } from "services/tasks"
import { useResourceSubscription } from "../../contexts/NotificationsContext"
import { Search, Users, Plus, Star, User, Briefcase, Tag, X } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Input } from "../ui/input"
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet"
import { ContactRow } from "./ContactRow"
import { ContactListSkeleton } from "./ContactListSkeleton"
import { TooltipProvider } from "../ui/tooltip"
import { ContactDetailPanel, type Contact, type Task } from "./ContactDetailPanel"
import { ContactFormModal, type ContactFormHandle } from "./NewContactModal"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog"
import { getTagChipStyles } from "../../utils/tagHelpers"
import { useTheme } from "../../contexts/ThemeContext"
import "./contacts-responsive.css"

// Cubone (file-manager) usa elementi <dialog> nativi (`.fm-modal`) e un
// context menu (`.fm-context-menu`) renderizzati nel subtree del FileManager.
// Senza questo guard, qualunque click su quegli elementi farebbe scattare
// `onPointerDownOutside` di Radix Sheet e chiuderebbe il pannello.
const isFromCubone = (target: EventTarget | null) => {
  const t = target as HTMLElement | null
  if (!t) return false
  return Boolean(
    t.closest('[data-cubone-root]') ||
    t.closest('.fm-modal') ||
    t.closest('.fm-context-menu')
  )
}

export function ContattiContent() {
  const { theme } = useTheme()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [activeContactId, setActiveContactId] = useState<string | null>(null)
  const [openContactIds, setOpenContactIds] = useState<string[]>([]) // Future-proof for tabs
  const [sheetOpen, setSheetOpen] = useState(false)
  const [newContactModalOpen, setNewContactModalOpen] = useState(false)
  const [editContactModalOpen, setEditContactModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [favoriteError, setFavoriteError] = useState<string | null>(null)
  const favoriteRequests = useRef(new Set<string>())
  const newContactFormRef = useRef<ContactFormHandle>(null)
  const editContactFormRef = useRef<ContactFormHandle>(null)
  const [contactTypeFilter, setContactTypeFilter] = useState<"all" | "individual" | "company">("all")
  const [searchQuery, setSearchQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [linkedTasks, setLinkedTasks] = useState<Task[]>([])
  const [allTags, setAllTags] = useState<{id: number, name: string, color: string}[]>([])
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null)
  const [tagManageMode, setTagManageMode] = useState(false)
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null)
  const [isDeletingContact, setIsDeletingContact] = useState(false)
  const [deleteContactError, setDeleteContactError] = useState<string | null>(null)
  const [tagToDelete, setTagToDelete] = useState<{id: number, name: string, color: string} | null>(null)
  const [isDeletingTag, setIsDeletingTag] = useState(false)
  const [deleteTagError, setDeleteTagError] = useState<string | null>(null)

  const location = useLocation()

  // Apri il side-panel se si arriva dalla ricerca globale con openDetailId
  useEffect(() => {
    const state = location.state as { openDetailId?: number | string } | null
    if (state?.openDetailId && contacts.length > 0) {
      const id = String(state.openDetailId)
      const contact = contacts.find((c) => String(c.id) === id)
      if (contact) {
        setActiveContactId(id)
        setSheetOpen(true)
        if (!openContactIds.includes(id)) {
          setOpenContactIds((prev) => [...prev, id])
        }
      }
      window.history.replaceState({}, "")
    }
  }, [location.state, contacts])

  // Fetch contacts from API
  const fetchContacts = useCallback(async () => {
    try {
      setIsLoading(true)
      setLoadError(null)
      const response = await getContacts()
      const data = Array.isArray(response.data) ? response.data : []

      // Map API response to Contact interface
      const formattedContacts: Contact[] = data.map((c: any) => ({
        id: String(c.id),
        contact_type: c.contact_type || "individual",

        // Calcolati
        name: c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "N/A",
        status: c.is_active !== false ? "active" : "inactive",
        isFavorite: c.is_favorite || false,

        // Telefoni
        mobile_phone: c.mobile_phone || "",
        phone_2: c.phone_2 || null,
        phone_3: c.phone_3 || null,

        // Email
        email: c.email || null,
        email_2: c.email_2 || null,
        email_3: c.email_3 || null,
        pec_email: c.pec_email || null,

        // Persona fisica
        first_name: c.first_name || null,
        last_name: c.last_name || null,
        fiscal_code: c.fiscal_code || null,
        address: c.address || null,
        role: c.role || null,
        company_reference: c.company_reference || null,

        // Persona giuridica
        company_name: c.company_name || null,
        vat_number: c.vat_number || null,
        company_fiscal_code: c.company_fiscal_code || null,
        legal_address: c.legal_address || null,
        operational_address: c.operational_address || null,
        legal_representative_first_name: c.legal_representative_first_name || null,
        legal_representative_last_name: c.legal_representative_last_name || null,

        // Altri
        notes: c.notes || null,
        websites: c.websites || [],
        related_contacts: c.related_contacts || [],
        avatar_url: c.avatar_url || null,

        // Legacy UI
        tags: c.tags || [],
        hasPhone: !!c.mobile_phone,
        hasEmail: !!c.email,
        type: c.contact_type === "company" ? "company" : "person",
      }))

      setContacts(formattedContacts)
    } catch (error) {
      console.error("Error fetching contacts:", error)
      setLoadError("Impossibile caricare i contatti. Riprova.")
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchContacts()
  }, [fetchContacts])

  // Realtime: refetch quando l'agent (o un altro client) modifica i contatti
  useResourceSubscription('contact', () => fetchContacts())

  // Fetch tasks for linked contacts
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await getTasks()
        const data = Array.isArray(response.data) ? response.data : []

        const formattedTasks: Task[] = data.map((t: any) => ({
          id: String(t.id),
          title: t.title || t.name || "Task",
          description: t.description || "",
          priority: t.priority?.key?.toLowerCase() || t.priority || "medium",
          dueDate: t.due_date || t.deadline || "",
          status: t.status?.key?.toLowerCase() || t.status || "todo",
          linked_contacts: t.linked_contacts || [],
          assigned_to: t.assigned_to?.id ? String(t.assigned_to.id) : "",
        }))

        setLinkedTasks(formattedTasks)
      } catch (error) {
        console.error("Error fetching tasks:", error)
        setLinkedTasks([])
      }
    }

    fetchTasks()
  }, [])

  // Fetch tags from API
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await getTags()
        setAllTags(response.data || [])
      } catch (error) {
        console.error("Error fetching tags:", error)
        setAllTags([])
      }
    }

    fetchTags()
  }, [])

  // Calculate tag counts from contacts
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    contacts.forEach(contact => {
      if (contact.tags) {
        contact.tags.forEach(tag => {
          counts[tag.name] = (counts[tag.name] || 0) + 1
        })
      }
    })
    return counts
  }, [contacts])

  // Filter and sort contacts
  const filteredContacts = useMemo(() => {
    let result = contactTypeFilter === "all"
      ? [...contacts]
      : contacts.filter((contact) => contact.contact_type === contactTypeFilter)

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase()
      result = result.filter((contact) =>
        contact.name.toLowerCase().includes(query) ||
        contact.email?.toLowerCase().includes(query) ||
        contact.mobile_phone?.toLowerCase().includes(query)
      )
    }

    // Apply tag filter
    if (activeTagFilter) {
      result = result.filter(contact => contact.tags && contact.tags.some(tag => tag.name === activeTagFilter))
    }

    // Sort alphabetically by name
    result.sort((a, b) => a.name.localeCompare(b.name, 'it'))

    return result
  }, [contacts, contactTypeFilter, searchQuery, activeTagFilter])

  // Group contacts by first letter
  const groupedContacts = useMemo(() => {
    const groups: { letter: string; contacts: typeof filteredContacts }[] = []
    let currentLetter = ""

    filteredContacts.forEach((contact) => {
      const firstLetter = contact.name.charAt(0).toUpperCase()
      if (firstLetter !== currentLetter) {
        currentLetter = firstLetter
        groups.push({ letter: firstLetter, contacts: [contact] })
      } else {
        groups[groups.length - 1].contacts.push(contact)
      }
    })

    return groups
  }, [filteredContacts])

  const isEmpty = filteredContacts.length === 0
  const isEmptyDatabase = contacts.length === 0

  // Calculate stats
  const totalContacts = filteredContacts.length
  const personCount = contacts.filter((c) => c.type === "person").length
  const companyCount = contacts.filter((c) => c.type === "company").length
  const favoriteContacts = contacts.filter((c) => c.isFavorite === true)

  // Get active contact
  const activeContact = contacts.find((c) => c.id === activeContactId)

  // Toggle favorite
  const handleToggleFavorite = async (contactId: string) => {
    const contact = contacts.find(c => c.id === contactId)
    if (!contact || favoriteRequests.current.has(contactId)) return
    favoriteRequests.current.add(contactId)
    setFavoriteError(null)
    const nextFavorite = !contact.isFavorite
    try {
      await patchContact(contactId, { is_favorite: nextFavorite })
      setContacts((prev) =>
        prev.map((c) => (c.id === contactId ? { ...c, isFavorite: nextFavorite } : c))
      )
    } catch (error) {
      console.error("Errore toggle preferito:", error)
      setFavoriteError("Impossibile aggiornare i preferiti. Riprova.")
    } finally {
      favoriteRequests.current.delete(contactId)
    }
  }

  const handleNewContact = () => {
    setNewContactModalOpen(true)
  }

  const handleSaveNewContact = (newContact: any) => {
    // newContact è response.data dal backend (con id reale)
    const fullContact: Contact = {
      id: String(newContact.id),
      contact_type: newContact.contact_type || "individual",

      // Calcolati
      name: newContact.company_name || `${newContact.first_name || ""} ${newContact.last_name || ""}`.trim() || "N/A",
      status: "active",
      isFavorite: false,

      // Telefoni
      mobile_phone: newContact.mobile_phone || "",
      phone_2: newContact.phone_2 || null,
      phone_3: newContact.phone_3 || null,

      // Email
      email: newContact.email || null,
      email_2: newContact.email_2 || null,
      email_3: newContact.email_3 || null,
      pec_email: newContact.pec_email || null,

      // Persona fisica
      first_name: newContact.first_name || null,
      last_name: newContact.last_name || null,
      fiscal_code: newContact.fiscal_code || null,
      address: newContact.address || null,
      role: newContact.role || null,
      company_reference: newContact.company_reference || null,

      // Persona giuridica
      company_name: newContact.company_name || null,
      vat_number: newContact.vat_number || null,
      company_fiscal_code: newContact.company_fiscal_code || null,
      legal_address: newContact.legal_address || null,
      operational_address: newContact.operational_address || null,
      legal_representative_first_name: newContact.legal_representative_first_name || null,
      legal_representative_last_name: newContact.legal_representative_last_name || null,

      // Altri
      notes: newContact.notes || null,
      websites: newContact.websites || [],
      related_contacts: newContact.related_contacts || [],
      avatar_url: newContact.avatar_url || null,

      // Legacy UI
      tags: newContact.tags || [],
      hasPhone: !!newContact.mobile_phone,
      hasEmail: !!newContact.email,
      type: newContact.contact_type === "company" ? "company" : "person",
    }

    setContacts((prev) => [fullContact, ...prev])
    setAllTags(prev => [...prev, ...fullContact.tags.filter(tag => !prev.some(item => item.id === tag.id))])
    setNewContactModalOpen(false)
  }

  const handleContactClick = (contactId: string) => {
    setActiveContactId(contactId)
    setSheetOpen(true)
    
    // Future-proof: track opened contacts (for tabs feature)
    if (!openContactIds.includes(contactId)) {
      setOpenContactIds((prev) => [...prev, contactId])
    }
  }

  const handleCloseSheet = () => {
    setSheetOpen(false)
  }

  const requestDeleteContact = (contactId: string) => {
    setDeleteContactError(null)
    setContactToDelete(contacts.find((contact) => contact.id === contactId) || null)
  }

  const handleDeleteContact = async () => {
    if (!contactToDelete || isDeletingContact) return
    const contactId = contactToDelete.id
    setIsDeletingContact(true)
    setDeleteContactError(null)
    try {
      await deleteContact(contactId)
      setContacts((prev) => prev.filter((c) => c.id !== contactId))
      setOpenContactIds((prev) => prev.filter((id) => id !== contactId))
      if (activeContactId === contactId) {
        setSheetOpen(false)
        setActiveContactId(null)
      }
      setContactToDelete(null)
    } catch {
      setDeleteContactError("Impossibile eliminare il contatto. Riprova.")
    } finally {
      setIsDeletingContact(false)
    }
  }

  const handleUpdateContact = (updatedContact: Contact) => {
    setContacts((prev) =>
      prev.map((c) => (c.id === updatedContact.id ? updatedContact : c))
    )
  }

  const handleOpenEditModal = () => {
    // Chiudiamo il Detail e apriamo Edit
    setSheetOpen(false)
    setEditContactModalOpen(true)
  }

  // Handler per chiusura Edit modal (Annulla, X, Esci senza salvare)
  // → Riapre il Detail modal
  const handleEditModalClose = () => {
    setEditContactModalOpen(false)
    setSheetOpen(true)
  }

  const handleSaveEditedContact = (editedData: any) => {
    if (!activeContact) return

    // editedData è response.data dal backend — usa i nomi campi backend
    const updatedContact: Contact = {
      ...activeContact,
      // Identificazione
      id: String(editedData.id),
      contact_type: editedData.contact_type || activeContact.contact_type,
      type: editedData.contact_type === "company" ? "company" : "person",

      // Nome calcolato
      name: editedData.contact_type === "company"
        ? editedData.company_name || "N/A"
        : `${editedData.first_name || ""} ${editedData.last_name || ""}`.trim() || "N/A",

      // Telefoni
      mobile_phone: editedData.mobile_phone || "",
      phone_2: editedData.phone_2 || null,
      phone_3: editedData.phone_3 || null,

      // Email
      email: editedData.email || null,
      email_2: editedData.email_2 || null,
      email_3: editedData.email_3 || null,
      pec_email: editedData.pec_email || null,

      // Persona fisica
      first_name: editedData.first_name || null,
      last_name: editedData.last_name || null,
      fiscal_code: editedData.fiscal_code || null,
      address: editedData.address || null,
      role: editedData.role || null,
      company_reference: editedData.company_reference || null,

      // Persona giuridica
      company_name: editedData.company_name || null,
      vat_number: editedData.vat_number || null,
      company_fiscal_code: editedData.company_fiscal_code || null,
      legal_address: editedData.legal_address || null,
      operational_address: editedData.operational_address || null,
      legal_representative_first_name: editedData.legal_representative_first_name || null,
      legal_representative_last_name: editedData.legal_representative_last_name || null,

      // Altri
      notes: editedData.notes || null,
      websites: editedData.websites || [],
      related_contacts: editedData.related_contacts || [],
      avatar_url: editedData.avatar_url || null,

      // Campi UI legacy
      hasPhone: !!editedData.mobile_phone,
      hasEmail: !!editedData.email,
      status: activeContact.status, // Mantieni status esistente
      tags: editedData.tags || activeContact.tags,
    }

    handleUpdateContact(updatedContact)
    setAllTags(prev => [...prev, ...updatedContact.tags.filter(tag => !prev.some(item => item.id === tag.id))])

    // Chiudi Edit e riapri Detail con i dati aggiornati
    setEditContactModalOpen(false)
    setSheetOpen(true)
  }

  const handleOpenTaskModal = (task: Task) => {
    console.log("[v0] Opening task modal for task:", task)
    // TODO: Implement task modal opening logic
    // This will be connected to TaskPageNew modal in future integration
  }

  return (
    <div className="contacts-page flex min-w-0 flex-col h-full pb-24">
      {/* Page Header Section - CTA Area */}
      <div className="w-full max-w-[1280px] mx-auto px-3 sm:px-6 pt-4 pb-3">
        <div className="flex items-center justify-end">
          <Button
            onClick={handleNewContact}
            className="bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Nuovo contatto
          </Button>
        </div>
      </div>

      {/* Main Content Area - fills remaining space */}
      <div className="flex-1 min-h-0 min-w-0 w-full max-w-[1280px] mx-auto px-3 sm:px-6 pb-4 flex flex-col">
        {/* Search and controls */}
        <div className="flex flex-wrap items-center gap-3 mb-3">
          {/* Search field - compact */}
          <div className="relative min-w-0 w-full sm:flex-1 sm:max-w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--hexa-text-muted)]" />
            <Input
              ref={searchInputRef}
              placeholder="Cerca..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Cerca contatti"
              className="h-10 pl-10 pr-10 bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] rounded-lg"
            />
            {searchQuery && (
              <button
                aria-label="Azzera ricerca contatti"
                onClick={() => { setSearchQuery(""); searchInputRef.current?.focus() }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Toggle Persone/Aziende */}
          <div className="inline-flex rounded-lg bg-[var(--hexa-card-bg)] border border-[var(--hexa-border)] p-[3px]">
            <button
              onClick={() => setContactTypeFilter("all")}
              className={`w-8 h-7 rounded-md flex items-center justify-center transition ${
                contactTypeFilter === "all"
                  ? "bg-[var(--hexa-purple)] text-white"
                  : "text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)]"
              }`}
              title="Tutti"
              aria-label="Tutti i contatti"
              aria-pressed={contactTypeFilter === "all"}
            >
              <Users className="w-[15px] h-[15px]" />
            </button>
            <button
              onClick={() => setContactTypeFilter("individual")}
              className={`w-8 h-7 rounded-md flex items-center justify-center transition ${
                contactTypeFilter === "individual"
                  ? "bg-[var(--hexa-purple)] text-white"
                  : "text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)]"
              }`}
              title="Persone"
              aria-label="Persone"
              aria-pressed={contactTypeFilter === "individual"}
            >
              <User className="w-[15px] h-[15px]" />
            </button>
            <button
              onClick={() => setContactTypeFilter("company")}
              className={`w-8 h-7 rounded-md flex items-center justify-center transition ${
                contactTypeFilter === "company"
                  ? "bg-[var(--hexa-purple)] text-white"
                  : "text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)]"
              }`}
              title="Aziende"
              aria-label="Aziende"
              aria-pressed={contactTypeFilter === "company"}
            >
              <Briefcase className="w-[15px] h-[15px]" />
            </button>
          </div>

        </div>

        {/* Stats bar */}
        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 sm:px-5 py-2 mb-3 rounded-lg text-[11px] border hexa-card-shadow"
          style={{
            backgroundColor: "var(--hexa-card-bg)",
            borderColor: "var(--hexa-border)",
          }}
        >
          <span className="font-semibold text-[var(--hexa-text-primary)]">
            CONTATTI <span className="font-bold">{totalContacts}</span>
          </span>
          <div className="w-px h-4" style={{ backgroundColor: "var(--hexa-border)" }} />
          <span className="text-[var(--hexa-text-secondary)]">
            {personCount} persone
          </span>
          <div className="w-px h-4" style={{ backgroundColor: "var(--hexa-border)" }} />
          <span className="text-[var(--hexa-text-secondary)]">
            {companyCount} aziende
          </span>
          <div className="w-px h-4" style={{ backgroundColor: "var(--hexa-border)" }} />
          <span className="flex items-center gap-1 text-[var(--hexa-text-secondary)]">
            <span className="w-2 h-2 rounded-full bg-yellow-400" />
            {favoriteContacts.length} preferiti
          </span>
        </div>

        {/* Tag filter banner */}
        {activeTagFilter && (
          <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-1.5 bg-[rgba(123,63,255,0.12)] border border-[rgba(123,63,255,0.3)] rounded-lg text-[11px] text-[var(--hexa-text-secondary)]">
            <span>Filtro tag:</span>
            <span className="inline-flex min-w-0 items-center gap-1.5 font-medium break-words">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: allTags.find(t => t.name === activeTagFilter)?.color || 'var(--hexa-purple)' }}
              />
              <span className="text-[var(--hexa-text-primary)]">{activeTagFilter}</span>
            </span>
            <button
              onClick={() => setActiveTagFilter(null)}
              className="w-4 h-4 rounded flex items-center justify-center bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.2)] text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] transition"
              title="Rimuovi filtro"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}

        {/* Main content - two columns */}
        <div className="flex-1 min-h-0 min-w-0 flex flex-col xl:flex-row gap-4">
          {/* Left column - Contact list */}
          <div className="flex-1 min-h-0 min-w-0 flex flex-col">
            {favoriteError && <p role="alert" className="mb-3 text-sm text-red-500">{favoriteError}</p>}
            {isLoading ? (
              <ContactListSkeleton count={5} />
            ) : loadError ? (
              <Card className="p-6 bg-[var(--hexa-card-bg)] border-[var(--hexa-border)]">
                <p role="alert" className="mb-3 text-sm">{loadError}</p>
                <Button onClick={fetchContacts}>Riprova caricamento</Button>
              </Card>
            ) : isEmpty ? (
              <Card className="bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] hexa-card-shadow">
                <CardContent className="p-8 text-center">
                  <Users className="w-9 h-9 text-[var(--hexa-text-muted)] mx-auto mb-3" />
                  {isEmptyDatabase ? (
                    <>
                      <h3 className="text-base font-semibold text-[var(--hexa-text-primary)] mb-1.5">
                        Nessun contatto ancora
                      </h3>
                      <p className="text-xs text-[var(--hexa-text-secondary)] mb-4">
                        Inizia aggiungendo il tuo primo contatto
                      </p>
                      <Button
                        onClick={handleNewContact}
                        className="bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        Crea il tuo primo contatto
                      </Button>
                    </>
                  ) : (
                    <>
                      <h3 className="text-base font-semibold text-[var(--hexa-text-primary)] mb-1.5">
                        Nessun contatto trovato
                      </h3>
                      <p className="text-xs text-[var(--hexa-text-secondary)]">
                        Prova a modificare i filtri di ricerca per vedere più risultati
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="contacts-list xl:max-h-[calc(100dvh-265px)] bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] xl:overflow-y-auto scrollbar-hexa hexa-card-shadow">
                <TooltipProvider>
                  {groupedContacts.map((group) => (
                    <div key={group.letter}>
                      {/* Letter separator */}
                      <div className="flex items-center gap-2 px-4 py-1">
                        <span className="text-[10px] font-semibold" style={{ color: "var(--hexa-purple)" }}>
                          {group.letter}
                        </span>
                        <div className="flex-1 h-px" style={{ backgroundColor: "var(--hexa-border)" }} />
                      </div>
                      {group.contacts.map((contact) => (
                        <ContactRow
                          key={contact.id}
                          {...contact}
                          selected={activeContactId === contact.id}
                          onClick={() => handleContactClick(contact.id)}
                          onToggleFavorite={handleToggleFavorite}
                          onDelete={() => requestDeleteContact(contact.id)}
                        />
                      ))}
                    </div>
                  ))}
                </TooltipProvider>
              </Card>
            )}
          </div>

          {/* Right column - Sidebar insights */}
          <div className="contacts-insights order-first xl:order-last w-full xl:w-[260px] grid sm:grid-cols-2 xl:flex xl:flex-col gap-4 shrink-0 xl:max-h-[calc(100dvh-265px)]">
            {/* Tag card */}
            <Card className="flex-1 min-h-0 overflow-hidden flex flex-col !py-2 !gap-1 bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] hexa-card-shadow">
              <CardHeader className="!px-4 !pb-0 flex flex-row items-center justify-between space-y-0 flex-shrink-0">
                <CardTitle className="text-[10px] font-medium text-[var(--hexa-text-secondary)] uppercase tracking-wide flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" />
                  Tag
                </CardTitle>
                <button
                  type="button"
                  onClick={() => setTagManageMode(prev => !prev)}
                  className="text-[10px] text-[var(--hexa-purple)] hover:text-[var(--hexa-purple-hover)] transition"
                >
                  {tagManageMode ? "Fine" : "Gestisci"}
                </button>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 overflow-hidden !px-0">
                {allTags.length === 0 ? (
                  <div className="text-center py-4 px-6">
                    <p className="text-sm text-[var(--hexa-text-secondary)]">Nessun tag creato</p>
                  </div>
                ) : (
                  <div className="flex flex-wrap content-start items-start gap-2 max-h-40 overflow-y-auto scrollbar-hexa px-4 pb-2">
                    {allTags.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => !tagManageMode && setActiveTagFilter(prev => prev === tag.name ? null : tag.name)}
                        className="inline-flex min-w-0 max-w-full items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition hover:opacity-80"
                        style={{
                          ...getTagChipStyles(tag.color, theme, activeTagFilter === tag.name),
                          boxShadow: activeTagFilter === tag.name ? `0 0 0 2px ${tag.color}30` : 'none'
                        }}
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                        <span className="min-w-0 whitespace-normal break-words text-left">{tag.name}</span>
                        {tagCounts[tag.name] ? (
                          <span className="ml-0.5 opacity-70">{tagCounts[tag.name]}</span>
                        ) : null}
                        {tagManageMode && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation()
                              setTagToDelete(tag)
                              setDeleteTagError(null)
                            }}
                            className="ml-1 hover:opacity-100 opacity-60 cursor-pointer"
                          >
                            <X className="w-3 h-3" />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Favorite contacts card */}
            <Card className="flex-1 min-h-0 overflow-hidden flex flex-col !py-2 !gap-1 bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] hexa-card-shadow">
              <CardHeader className="!px-4 !pb-0 flex flex-row items-center justify-between space-y-0 flex-shrink-0">
                <CardTitle className="text-[10px] font-medium uppercase tracking-wider flex items-center gap-1.5" style={{ color: "var(--hexa-text-muted)" }}>
                  <span className="text-[13px]" style={{ color: "#facc15" }}>★</span>
                  Preferiti
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 overflow-hidden !px-0">
                {favoriteContacts.length === 0 ? (
                  <div className="text-center py-4 px-6 flex flex-col items-center gap-1">
                    <Star className="w-4 h-4 text-[var(--hexa-text-muted)]" />
                    <p className="text-[10px] text-[var(--hexa-text-muted)]">Nessun preferito</p>
                  </div>
                ) : (
                  <div className="flex flex-wrap content-start items-start gap-2 max-h-40 overflow-y-auto scrollbar-hexa px-4 pb-2">
                    {favoriteContacts.map((contact) => {
                      const initials = contact.name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)
                      const hash = contact.name.split("").reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0)
                      const colors = ["#d9cb2d", "#a738ff", "#e8d4fd", "#837fe0", "#05df72", "#51a2ff"]
                      const bgColor = colors[hash % colors.length]
                      return (
                        <button
                          key={contact.id}
                          onClick={() => handleContactClick(contact.id)}
                          className="flex min-w-0 max-w-full items-center gap-1.5 py-1 px-2.5 rounded-full border cursor-pointer transition-colors hover:border-[var(--hexa-text-muted)]"
                          style={{
                            borderColor: "var(--hexa-border)",
                            backgroundColor: "rgba(255,255,255,0.05)",
                          }}
                        >
                          <span
                            className="w-4 h-4 rounded-full flex items-center justify-center text-[7px] font-semibold text-white flex-shrink-0"
                            style={{ backgroundColor: bgColor }}
                          >
                            {initials}
                          </span>
                          <span className="min-w-0 break-words text-[10px]" style={{ color: "var(--hexa-text-secondary)" }}>
                            {contact.name}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Contact Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          className="contact-sheet w-full sm:max-w-[450px] h-[100dvh] min-w-0 p-0 bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]"
          onPointerDownOutside={(e) => {
            if (isFromCubone(e.target)) e.preventDefault()
          }}
          onInteractOutside={(e) => {
            if (isFromCubone(e.target)) e.preventDefault()
          }}
        >
          <SheetTitle className="sr-only">Dettaglio Contatto</SheetTitle>
          <SheetDescription className="sr-only">Visualizza dettaglio contatto</SheetDescription>
          {activeContact && (
            <div
              className="h-full min-h-0 min-w-0"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
            >
              <ContactDetailPanel
                contact={activeContact}
                onClose={handleCloseSheet}
                onUpdateContact={handleUpdateContact}
                onDeleteContact={requestDeleteContact}
                onOpenEditModal={handleOpenEditModal}
                tasks={linkedTasks}
                onOpenTaskModal={handleOpenTaskModal}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit Contact Modal Sheet */}
      <Sheet open={editContactModalOpen} onOpenChange={(open) => {
        if (!open) {
          // Quando lo Sheet si chiude (click fuori, ESC, ecc.), riapri Detail
          editContactFormRef.current?.requestClose()
        }
      }}>
        <SheetContent className="contact-sheet w-full sm:max-w-[450px] h-[100dvh] min-w-0 p-0 bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]">
          <SheetTitle className="sr-only">Modifica Contatto</SheetTitle>
          <SheetDescription className="sr-only">Modifica i dati del contatto</SheetDescription>
          {activeContact && (
            <ContactFormModal
              ref={editContactFormRef}
              mode="edit"
              contactId={activeContact.id}
              existingContact={activeContact}
              onClose={handleEditModalClose}
              onSave={handleSaveEditedContact}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* New Contact Modal Sheet */}
      <Sheet open={newContactModalOpen} onOpenChange={(open) => {
        if (!open) newContactFormRef.current?.requestClose()
      }}>
        <SheetContent className="contact-sheet w-full sm:max-w-[450px] h-[100dvh] min-w-0 p-0 bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]">
          <SheetTitle className="sr-only">Nuovo Contatto</SheetTitle>
          <SheetDescription className="sr-only">Crea un nuovo contatto</SheetDescription>
          <ContactFormModal
            ref={newContactFormRef}
            mode="create"
            onClose={() => setNewContactModalOpen(false)}
            onSave={handleSaveNewContact}
            availableContacts={contacts.map(c => ({ id: c.id, name: c.name, type: c.type }))}
          />
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!contactToDelete} onOpenChange={(open) => {
        if (!open && !isDeletingContact) setContactToDelete(null)
      }}>
        <AlertDialogContent className="bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il contatto?</AlertDialogTitle>
            <AlertDialogDescription className="break-words [overflow-wrap:anywhere]">
              Vuoi eliminare {contactToDelete?.name}? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteContactError && <p role="alert" className="text-sm text-red-500">{deleteContactError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingContact}>Annulla</AlertDialogCancel>
            <Button variant="destructive" disabled={isDeletingContact} onClick={handleDeleteContact}>
              {isDeletingContact ? "Eliminazione..." : "Elimina contatto"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Tag Confirmation Dialog */}
      <AlertDialog open={!!tagToDelete} onOpenChange={(open) => {
        if (!open && !isDeletingTag) setTagToDelete(null)
      }}>
        <AlertDialogContent className="bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--hexa-text-primary)]">Eliminare il tag?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--hexa-text-secondary)]">
              Il tag "{tagToDelete?.name}" verrà rimosso da {tagCounts[tagToDelete?.name || ''] || 0} contatti. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteTagError && <p role="alert" className="text-sm text-red-500">{deleteTagError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingTag} className="bg-[var(--hexa-button-cancel)] text-[var(--hexa-button-cancel-text)] hover:bg-[var(--hexa-button-cancel-hover)] border-none">
              Annulla
            </AlertDialogCancel>
            <Button
              disabled={isDeletingTag}
              className="bg-red-600 hover:bg-red-700 text-white border-none"
              onClick={async () => {
                if (tagToDelete && !isDeletingTag) {
                  setIsDeletingTag(true)
                  setDeleteTagError(null)
                  try {
                    await deleteTag(tagToDelete.id)
                    setAllTags(prev => prev.filter(t => t.id !== tagToDelete.id))
                    setContacts(prev => prev.map(contact => ({
                      ...contact, tags: contact.tags.filter(tag => tag.id !== tagToDelete.id),
                    })))
                    if (activeTagFilter === tagToDelete.name) setActiveTagFilter(null)
                    setTagToDelete(null)
                  } catch (error) {
                    console.error("Error deleting tag:", error)
                    setDeleteTagError("Impossibile eliminare il tag. Riprova.")
                  } finally {
                    setIsDeletingTag(false)
                  }
                }
              }}
            >
              {isDeletingTag ? "Eliminazione..." : "Elimina"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
